import { uniq } from 'lodash/fp';
import { useMemo, VFC } from 'react';

import ReactSelect from 'dashboard/components/ReactSelect';
import ParamLabel from 'dashboard/components/WorkflowControlPanel/ParamLabel';
import useWorkflowError from 'dashboard/components/WorkflowControlPanel/useWorkflowError';
import {
  caseGetSteps,
  filterUsableObjects,
  UsableObjectConfig,
} from 'dashboard/lib/workflows/objects';
import {
  WorkflowActionMeta,
  WorkflowError,
  WorkflowFieldObjectKey,
} from 'dashboard/models/workflow';
import {
  WorkflowActionCreateCaseV1,
  WorkflowActionResolveCaseV1,
  WorkflowStep,
} from 'dashboard/models/workflow-version';
import Spacer from 'design-system/components/Spacer';

interface ParamsResolveCaseV1Props {
  caseTemplates: WorkflowActionMeta['case-templates'];
  editable: boolean;
  errors: WorkflowError[];
  focusStep: (token: string) => void;
  usableObjects: UsableObjectConfig[];
  pathToAction: WorkflowStep[];
  params: WorkflowActionResolveCaseV1['params'];
  onMenuClose: () => void;
  updateActionParams: (newParams: WorkflowActionResolveCaseV1['params']) => void;
}

const ParamsResolveCaseV1: VFC<ParamsResolveCaseV1Props> = ({
  caseTemplates,
  editable,
  errors,
  focusStep,
  usableObjects,
  pathToAction,
  params,
  onMenuClose,
  updateActionParams,
}) => {
  const cases = useMemo(() => {
    return filterUsableObjects({
      usableObjects,
      options: {
        permittedObjects: [WorkflowFieldObjectKey.Case],
        excludeNonPersonaObjects: true,
      },
    });
  }, [usableObjects]);
  const selectCase = (kase: WorkflowActionResolveCaseV1['params']['case']) => {
    updateActionParams({
      ...params,
      case: kase,
    });
  };

  const possibleResolutions = useMemo(() => {
    const determinedCaseTemplate = caseGetSteps(pathToAction).find((c) => c.token === params.case);

    if (determinedCaseTemplate) {
      const caseToken = (determinedCaseTemplate.action as WorkflowActionCreateCaseV1).params[
        'case-template-token'
      ];
      const resolutions = caseTemplates.find(({ token }) => token === caseToken)?.resolutions;
      if (resolutions) {
        return resolutions.map((res) => ({ label: res, value: res }));
      }
    }

    /*
      Return all resolutions when case template is determined
      When params.case is trigger or previously created case which didn't select case template yet
    */
    return uniq(Object.values(caseTemplates).flatMap((template) => template.resolutions)).map(
      (res) => ({
        label: res,
        value: res,
      }),
    );
  }, [pathToAction, params.case, caseTemplates]);

  const selectResolution = (resolution: WorkflowActionResolveCaseV1['params']['resolution']) => {
    updateActionParams({
      ...params,
      resolution,
    });
  };

  const { hasErrorFor, renderErrorMessageFor } =
    useWorkflowError<keyof WorkflowActionResolveCaseV1['params']>(errors);

  return (
    <>
      <Spacer height={2} />
      <ParamLabel labelText='Case' htmlFor='params-resolve-case__case-input' />
      <ReactSelect
        value={params.case}
        options={cases}
        onChange={selectCase}
        id='params-resolve-case__case'
        disabled={!editable}
        onFocusOption={focusStep}
        onMenuClose={onMenuClose}
        hasError={hasErrorFor('case')}
      />
      {renderErrorMessageFor('case')}
      <Spacer height={2} />
      <ParamLabel labelText='Resolution' htmlFor='params-resolve-case__resolution-input' />
      <ReactSelect
        value={params.resolution}
        options={possibleResolutions}
        onChange={selectResolution}
        id='params-resolve-case__resolution'
        disabled={!editable}
        hasError={hasErrorFor('resolution')}
      />
      {renderErrorMessageFor('resolution')}
    </>
  );
};

export default ParamsResolveCaseV1;
